import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, apiUrl } from "../lib/api";
import type {
  Connection,
  ConnectionCapability,
  Provider,
  ProviderCapability,
  ProviderCapabilityGrant,
  PublicProviderResource,
  PublicProviderResourceAssignment,
  ProviderResourceAssignmentRole,
} from "../../../shared/types";

interface ConnectionListResponse {
  data: Connection[];
}

function accountConnectionsQueryPath(brandId?: string | null): string {
  if (!brandId) return "/api/connections";
  return `/api/connections?brand_id=${encodeURIComponent(brandId)}`;
}

export function useAccountConnections(brandId?: string | null) {
  return useQuery({
    queryKey: ["account-connections", brandId ?? null],
    queryFn: () => api.get<ConnectionListResponse>(accountConnectionsQueryPath(brandId)),
  });
}

export function useDisconnectConnection() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (connectionId: string) =>
      api.delete<{ data: { success: boolean } }>(`/api/connections/${encodeURIComponent(connectionId)}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["account-connections"] });
    },
  });
}

export function useAssignConnectionToBrand() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { connectionId: string; brandId: string }) =>
      api.post<{ data: Connection }>(
        `/api/connections/${encodeURIComponent(input.connectionId)}/assignments`,
        { brand_id: input.brandId },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["account-connections"] });
    },
  });
}

export function useRemoveConnectionAssignment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { connectionId: string; brandId: string }) =>
      api.delete<{ data: { success: boolean } }>(
        `/api/connections/${encodeURIComponent(input.connectionId)}/assignments/${encodeURIComponent(input.brandId)}`,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["account-connections"] });
    },
  });
}

export function connectLinkedIn(brandId?: string | null) {
  const params = new URLSearchParams();
  if (brandId) params.set("brand_id", brandId);
  const query = params.toString();
  window.location.href = apiUrl(`/api/connections/linkedin/authorize${query ? `?${query}` : ""}`);
}

export function useBlueskyReadiness() {
  return useQuery({
    queryKey: ["bluesky-oauth-readiness"],
    queryFn: () => api.get<{ data: { runnable: boolean; reason: string | null } }>("/api/connections/bluesky/readiness"),
    staleTime: 60_000,
  });
}

export function connectBluesky(handle: string, brandId?: string | null) {
  const params = new URLSearchParams();
  params.set("handle", handle.trim());
  if (brandId) params.set("brand_id", brandId);
  const query = params.toString();
  window.location.href = apiUrl(`/api/connections/bluesky/authorize${query ? `?${query}` : ""}`);
}

export function useMetaFacebookPagesReadiness() {
  return useQuery({
    queryKey: ["meta-facebook-pages-readiness"],
    queryFn: () => api.get<{ data: { analytics: boolean; publishing: boolean; reason: string | null } }>("/api/connections/meta/facebook/pages/readiness"),
    staleTime: 60_000,
  });
}

export function useInstagramReadiness() {
  return useQuery({ queryKey: ["instagram-readiness"], queryFn: () => api.get<{ data: { analytics: boolean; publishing: false; reason: string | null } }>("/api/connections/instagram/readiness"), staleTime: 60_000 });
}

export async function connectInstagram(brandId?: string | null) {
  const params = new URLSearchParams(); if (brandId) params.set("brand_id", brandId);
  const response = await api.get<{ data: { authorization_url: string } }>(`/api/connections/instagram/authorize${params.toString() ? `?${params}` : ""}`);
  const url = new URL(response.data.authorization_url); if (url.protocol !== "https:" || url.hostname !== "www.instagram.com") throw new Error("Instagram authorization URL was invalid"); window.location.href = url.toString();
}

export async function getMetaFacebookPagesAuthorizationUrl(path: string, brandId?: string | null): Promise<string> {
  const params = new URLSearchParams();
  if (brandId) params.set("brand_id", brandId);
  const query = params.toString();
  const response = await api.get<{ data: { authorization_url: string } }>(`${path}${query ? `?${query}` : ""}`);
  const url = new URL(response.data.authorization_url);
  if (url.protocol !== "https:" || url.hostname !== "www.facebook.com") throw new Error("Facebook authorization URL was invalid");
  return url.toString();
}

export async function connectFacebookPages(brandId?: string | null) {
  window.location.href = await getMetaFacebookPagesAuthorizationUrl("/api/connections/meta/facebook/pages/authorize", brandId);
}

export async function connectFacebookPagesPublishing(brandId?: string | null) {
  window.location.href = await getMetaFacebookPagesAuthorizationUrl("/api/connections/meta/facebook/pages/publish/authorize", brandId);
}

// ── Google provider connections (PRD-0118 Parents 3–7) ──
//
// Google connections are account-owned authorizations that expose one or more
// separately consented capabilities (Search Console read, Analytics read, …).
// A capability is its own OAuth grant; connecting Search Console does not
// connect GA4. Resources (e.g. a Search Console property) are discovered
// through the connection and assigned to individual brands — the connection
// itself never grants blanket access to every brand.

/** The Search Console read capability key. */
export const GOOGLE_SEARCH_CONSOLE_CAPABILITY: ProviderCapability = "google.search_console.read";

/** The Google Analytics 4 read capability key. */
export const GOOGLE_ANALYTICS_CAPABILITY: ProviderCapability = "google.analytics.read";
export const GOOGLE_YOUTUBE_ANALYTICS_CAPABILITY: ProviderCapability = "google.youtube.analytics.read";

/**
 * Start the Google OAuth flow for a capability. The backend issues a durable
 * one-time state, selects an allowlisted redirect URI, and returns the Google
 * authorization URL. After consent, the callback exchanges the code, stores
 * the refresh token in Mech Vault (never in the DB), and redirects back here.
 */
export async function getGoogleCapabilityAuthorizationUrl(
  capability: ProviderCapability,
  brandId?: string | null,
): Promise<string> {
  const params = new URLSearchParams();
  if (brandId) params.set("brand_id", brandId);
  const query = params.toString();
  const response = await api.get<{ data: { authorizationUrl: string } }>(
    `/api/connections/google/capabilities/${encodeURIComponent(capability)}/authorize${query ? `?${query}` : ""}`,
  );
  const authorizationUrl = new URL(response.data.authorizationUrl);
  if (authorizationUrl.protocol !== "https:" || authorizationUrl.hostname !== "accounts.google.com") {
    throw new Error("Google authorization URL was invalid");
  }
  return authorizationUrl.toString();
}

export async function connectGoogleCapability(capability: ProviderCapability, brandId?: string | null) {
  window.location.href = await getGoogleCapabilityAuthorizationUrl(capability, brandId);
}

/** Convenience wrapper for the most common capability. */
export function connectGoogleSearchConsole(brandId?: string | null) {
  return connectGoogleCapability(GOOGLE_SEARCH_CONSOLE_CAPABILITY, brandId);
}

/** Convenience wrapper for the Google Analytics 4 capability. */
export function connectGoogleAnalytics(brandId?: string | null) {
  return connectGoogleCapability(GOOGLE_ANALYTICS_CAPABILITY, brandId);
}

/** YouTube is a separate Google consent/connection path. */
export function connectYouTube(brandId?: string | null) {
  return connectGoogleCapability(GOOGLE_YOUTUBE_ANALYTICS_CAPABILITY, brandId);
}

/** Capability grants for one connection (status: available/connected/expired/error/unsupported/revoked). */
export function useConnectionCapabilities(connectionId: string | null | undefined) {
  return useQuery({
    queryKey: ["connection-capabilities", connectionId ?? null],
    queryFn: () =>
      api.get<{ data: Array<{ capability: ConnectionCapability | null; status: string | null; granted_scopes: string[] | null; expires_at: string | null }> }>(
        `/api/connections/${encodeURIComponent(connectionId!)}/capabilities`,
      ),
    enabled: !!connectionId,
  });
}

/** Resources discovered through a connection (e.g. Search Console properties). */
export function useConnectionResources(connectionId: string | null | undefined) {
  return useQuery({
    queryKey: ["connection-resources", connectionId ?? null],
    queryFn: () =>
      api.get<{ data: PublicProviderResource[] }>(`/api/connections/${encodeURIComponent(connectionId!)}/resources`),
    enabled: !!connectionId,
  });
}

/** Resource→brand assignments under a connection (owner-scoped, read-only). */
export function useConnectionResourceAssignments(connectionId: string | null | undefined) {
  return useQuery({
    queryKey: ["connection-resource-assignments", connectionId ?? null],
    queryFn: () =>
      api.get<{ data: PublicProviderResourceAssignment[] }>(
        `/api/connections/${encodeURIComponent(connectionId!)}/resource-assignments`,
      ),
    enabled: !!connectionId,
  });
}

/** Re-run provider resource discovery (idempotent). The backend refreshResources
 * handler requires `capability` in the body (400 VALIDATION_ERROR without it) and
 * returns the refreshed `{ data: PublicProviderResource[] }` — it does NOT return a
 * `{ success }` envelope, and resources are not auto-discovered during the OAuth
 * callback, so this mutation is the only way properties enter the UI. */
export function useDiscoverResources() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { connectionId: string; capability: ProviderCapability }) =>
      api.post<{ data: PublicProviderResource[] }>(
        `/api/connections/${encodeURIComponent(input.connectionId)}/resources/discover`,
        { capability: input.capability },
      ),
    onSuccess: (_data, input) => {
      qc.invalidateQueries({ queryKey: ["connection-resources", input.connectionId] });
      qc.invalidateQueries({ queryKey: ["account-connections"] });
    },
  });
}

/** Assign one provider resource to a brand (many-to-many; analytics_enabled defaults true). */
export function useAssignResourceToBrand() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      connectionId: string;
      resourceId: string;
      brandId: string;
      role: ProviderResourceAssignmentRole;
      analyticsEnabled?: boolean;
    }) =>
      api.post<{ data: PublicProviderResourceAssignment }>(
        `/api/connections/${encodeURIComponent(input.connectionId)}/resources/${encodeURIComponent(input.resourceId)}/assignments`,
        {
          brand_id: input.brandId,
          role: input.role,
          analytics_enabled: input.analyticsEnabled ?? true,
        },
      ),
    onSuccess: (_data, input) => {
      qc.invalidateQueries({ queryKey: ["account-connections"] });
      qc.invalidateQueries({ queryKey: ["connection-resources", input.connectionId] });
      qc.invalidateQueries({ queryKey: ["connection-resource-assignments", input.connectionId] });
    },
  });
}

/** End a resource assignment (closes the validity interval; preserves historical facts). */
export function useEndResourceAssignment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { connectionId: string; resourceId: string; assignmentId: string }) =>
      api.delete<{ data: PublicProviderResourceAssignment }>(
        `/api/connections/${encodeURIComponent(input.connectionId)}/resources/${encodeURIComponent(input.resourceId)}/assignments/${encodeURIComponent(input.assignmentId)}`,
      ),
    onSuccess: (_data, input) => {
      qc.invalidateQueries({ queryKey: ["account-connections"] });
      qc.invalidateQueries({ queryKey: ["connection-resources", input.connectionId] });
      qc.invalidateQueries({ queryKey: ["connection-resource-assignments", input.connectionId] });
    },
  });
}

/** Group a flat connection list by provider for the Connections UI. */
export function connectionsByProvider(connections: Connection[]): Map<Provider, Connection[]> {
  const groups = new Map<Provider, Connection[]>();
  for (const connection of connections) {
    const provider = connection.provider ?? (connection.platform === "linkedin" ? "linkedin" : connection.platform === "shopify" ? "shopify" : "google");
    const bucket = groups.get(provider) ?? [];
    bucket.push(connection);
    groups.set(provider, bucket);
  }
  return groups;
}
